import { MapPin } from "lucide-react";
import { useLocationOptions, findOptionMatch, optionLabel, withSelectedOption } from "@/lib/locations";
import { sanitizeLocationValue } from "@/lib/locationSanitizers";
import FilterField from "@/components/ui/FilterField";
import { MultiSelect } from "@/components/ui/MultiSelect";
import type { LocationValue } from "@/lib/locations";

interface Props {
  value: LocationValue;
  onChange: (next: LocationValue) => void;
  context?: { category?: string; subcategory?: string; q?: string };
  variant?: "listing" | "hero";
}

export default function SearchLocationFields({ value, onChange, context = {}, variant = "listing" }: Props): React.JSX.Element {
  const cleanValue: LocationValue = {
    state: sanitizeLocationValue(value.state),
    city: sanitizeLocationValue(value.city),
    neighborhood: sanitizeLocationValue(value.neighborhood),
  };

  const options = useLocationOptions(cleanValue, context);

  const isHero = variant === "hero";
  const selectStyles = "!border-none !bg-transparent !p-0 !shadow-none !min-h-0 !h-auto";

  const update = (field: string, nextValue: string) => {
    const next = { ...cleanValue, [field]: nextValue };
    if (field === "state") {
      next.city = "";
      next.neighborhood = "";
    }
    if (field === "city") next.neighborhood = "";
    onChange(next);
  };

  const stateOptions = withSelectedOption(options.states, cleanValue.state || "");
  const cityOptions = withSelectedOption(options.cities, cleanValue.city || "");
  const neighborhoodOptions = withSelectedOption(options.neighborhoods, cleanValue.neighborhood || "");

  const resolvedState = findOptionMatch(stateOptions, cleanValue.state || "")?.value || cleanValue.state || "";
  const resolvedCity = findOptionMatch(cityOptions, cleanValue.city || "")?.value || cleanValue.city || "";
  const resolvedNeighborhood = findOptionMatch(neighborhoodOptions, cleanValue.neighborhood || "")?.value || cleanValue.neighborhood || "";

  const showNeighborhood = !!cleanValue.state && !!cleanValue.city;

  if (isHero) {
    return (
      <>
        <div className="flex-none flex items-center gap-2.5 px-4 py-2 rounded-2xl md:rounded-none md:border-r border-cream-200 min-w-0">
          <MapPin className="w-4 h-4 flex-shrink-0 text-heat-500" />
          <MultiSelect
            options={stateOptions.map(o => ({ label: optionLabel(o), value: o.value }))}
            selected={resolvedState ? [resolvedState] : []}
            onChange={(vals) => update("state", vals[0] || "")}
            multiple={false}
            placeholder="Estado"
            className={selectStyles}
            data-testid="hero-state-select"
          />
        </div>
        <div className="flex-none flex items-center gap-2.5 px-4 py-2 rounded-2xl md:rounded-none md:border-r border-cream-200 min-w-0">
          <MapPin className="w-4 h-4 flex-shrink-0 text-heat-500" />
          <MultiSelect
            options={cityOptions.map(o => ({ label: optionLabel(o), value: o.value }))}
            selected={resolvedCity ? [resolvedCity] : []}
            onChange={(vals) => update("city", vals[0] || "")}
            multiple={false}
            placeholder="Cidade"
            className={selectStyles}
            data-testid="hero-city-select"
          />
        </div>
        {showNeighborhood && (
          <div className="flex-none flex items-center gap-2.5 px-4 py-2 rounded-2xl md:rounded-none md:border-r border-cream-200 min-w-0">
            <MapPin className="w-4 h-4 flex-shrink-0 text-heat-500" />
            <MultiSelect
              options={neighborhoodOptions.map(o => ({ label: optionLabel(o), value: o.value }))}
              selected={resolvedNeighborhood ? [resolvedNeighborhood] : []}
              onChange={(vals) => update("neighborhood", vals[0] || "")}
              multiple={false}
              placeholder="Bairro"
              className={selectStyles}
              data-testid="hero-neighborhood-select"
            />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <FilterField label="Estado" variant={variant}>
        <MultiSelect
          options={stateOptions.map(o => ({ label: optionLabel(o), value: o.value }))}
          selected={resolvedState ? [resolvedState] : []}
          onChange={(vals) => update("state", vals[0] || "")}
          multiple={false}
          placeholder="Todos"
          className={selectStyles}
          data-testid="filter-state"
        />
      </FilterField>

      <FilterField label="Cidade" variant={variant}>
        <MultiSelect
          options={cityOptions.map(o => ({ label: optionLabel(o), value: o.value }))}
          selected={resolvedCity ? [resolvedCity] : []}
          onChange={(vals) => update("city", vals[0] || "")}
          multiple={false}
          placeholder="Todas"
          className={selectStyles}
          data-testid="filter-city"
        />
      </FilterField>

      {showNeighborhood && (
        <FilterField label="Bairro" variant={variant}>
          <MultiSelect
            options={neighborhoodOptions.map(o => ({ label: optionLabel(o), value: o.value }))}
            selected={resolvedNeighborhood ? [resolvedNeighborhood] : []}
            onChange={(vals) => update("neighborhood", vals[0] || "")}
            multiple={false}
            placeholder="Todos"
            className={selectStyles}
            data-testid="filter-neighborhood"
          />
        </FilterField>
      )}
    </>
  );
}